
import React from 'react';
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import FlightTerminalInfo from "./FlightTerminalInfo";
import { enUS, fr, es, it, de, ru, sv, pt, zhCN, ar } from "date-fns/locale";
import type { Locale } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

// Map of supported languages to date-fns locales
const localeMap: Record<string, Locale> = {
  en: enUS,
  fr: fr,
  es: es,
  it: it,
  de: de,
  ru: ru,
  sv: sv,
  pt: pt,
  zh: zhCN,
  ar: ar
};

interface FlightLocationDisplayProps {
  locationCode: string;
  locationName: string;
  time: string;
  timezone: string;
  terminal?: string;
  gate?: string;
  isArrival?: boolean;
  estimatedTime?: string | null;
  secondaryTime?: string | null;
  hasTimeDifference?: boolean;
  diffMinutes?: number;
  isCanceled?: boolean;
  timeDifferenceText?: string | null;
  actualArrivalTime?: string | null;
  airportName?: string;
  isFullScreen?: boolean;
}

const FlightLocationDisplay = ({
  locationCode,
  locationName,
  time,
  timezone,
  terminal,
  gate,
  isArrival = false,
  estimatedTime = null,
  secondaryTime = null,
  hasTimeDifference = false,
  diffMinutes = 0,
  isCanceled = false,
  timeDifferenceText = null,
  actualArrivalTime = null,
  airportName,
  isFullScreen = false
}: FlightLocationDisplayProps) => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  
  // Parse the date once for efficiency
  const dateObj = parseISO(time || "1970");
  
  // Get the appropriate locale based on the current language
  const locale = i18n.language && localeMap[i18n.language] ? localeMap[i18n.language] : undefined;
  
  // Get time color based on status
  const getTimeColor = () => {
    if (isCanceled) return 'text-red-500';
    if (diffMinutes > 0) return 'text-orange-500'; // Delayed
    if (diffMinutes < 0) return 'text-green-500'; // Early
    return 'text-gray-700'; // On time
  };
  
  // Format the scheduled time
  const scheduledTime = format(dateObj, 'HH:mm');
  
  // Check if estimated time is different from scheduled time
  const isEstimatedDifferent = estimatedTime && estimatedTime !== scheduledTime;
  
  return (
    <div className={`${isArrival ? 'text-right' : ''} w-[30%]`}>
      <div className="font-bold text-lg">{locationCode}</div>
      <div className="text-xs text-travel-400 min-h-[40px] w-full max-w-[160px]">
        <div className={isFullScreen ? '' : 'line-clamp-2'}>
          {airportName || locationName}
        </div>
      </div>
      <div className="text-xs text-travel-400 mt-1">
        {format(dateObj, 'EEE, MMM d', { locale })}
      </div>
      
      {/* Time display with support for estimated times */}
      {isCanceled ? (
        // Canceled flight
        <div className="text-xl font-bold text-red-500 line-through">
          {scheduledTime}
        </div>
      ) : isEstimatedDifferent ? (
        // Estimated time is different from scheduled time
        <div className="flex flex-col">
          <span className={`text-xl font-bold ${getTimeColor()}`}>
            {estimatedTime}
          </span>
          <span className="text-sm font-medium line-through text-gray-400">
            {scheduledTime}
          </span>
        </div>
      ) : (
        // No difference or no estimated time available
        <div className="text-travel-500 text-xl font-bold">
          {estimatedTime || scheduledTime}
        </div>
      )}
      {timezone && (<div className="text-xs text-travel-400 min-h-[40px] w-full max-w-[160px]">
        <div className={isFullScreen ? '' : 'line-clamp-2'}>
        ({timezone})
        </div>
      </div>)}
      
    </div>
  );
};

export default FlightLocationDisplay;
